import React from 'react';
import {
  Select,
  MenuItem,
  FormControl,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import { Language as LanguageIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../hooks/useLanguage';
import { SupportedLanguage } from '../lib/i18n';

interface LanguageSwitcherProps {
  variant?: 'default' | 'compact' | 'sidebar';
  showLabel?: boolean;
  className?: string;
}

// Flag component using flagcdn.com for reliable flag images
const Flag = ({ countryCode, size = 20 }: { countryCode: string; size?: number }) => (
  <img
    src={`https://flagcdn.com/w40/${countryCode.toLowerCase()}.png`}
    srcSet={`https://flagcdn.com/w80/${countryCode.toLowerCase()}.png 2x`}
    width={size}
    height={Math.round(size * 0.75)}
    alt={countryCode}
    style={{ borderRadius: 2, objectFit: 'cover' }}
  />
);

export default function LanguageSwitcher({ 
  variant = 'default', 
  showLabel = true,
  className 
}: LanguageSwitcherProps) {
  const { t } = useTranslation('common');
  const { currentLanguage, changeLanguage, supportedLanguages, isChanging } = useLanguage();

  const handleChange = (event: any) => {
    const newLang = event.target.value as SupportedLanguage;
    changeLanguage(newLang);
  };

  // Get flag code for a language code
  const getFlagCode = (code: string) => {
    const lang = supportedLanguages.find(l => l.code === code);
    return lang?.flagCode || 'UN';
  };

  if (variant === 'compact') {
    return (
      <FormControl size="small" className={className}>
        <Select
          value={currentLanguage}
          onChange={handleChange}
          disabled={isChanging}
          sx={{
            minWidth: 100,
            '& .MuiSelect-select': {
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              py: 0.5,
            },
          }}
        >
          {supportedLanguages.map((lang) => (
            <MenuItem key={lang.code} value={lang.code}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Flag countryCode={lang.flagCode} size={18} />
                <Typography variant="body2">{lang.code.toUpperCase()}</Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  if (variant === 'sidebar') {
    // Find current language data for display
    const currentLangData = supportedLanguages.find(l => l.code === currentLanguage);
    
    return (
      <Box 
        className={className}
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          px: 2,
          py: 1,
        }}
      >
        <Select
          value={currentLanguage}
          onChange={handleChange}
          disabled={isChanging}
          variant="standard"
          MenuProps={{
            anchorOrigin: {
              vertical: 'top',
              horizontal: 'left',
            },
            transformOrigin: {
              vertical: 'bottom',
              horizontal: 'left',
            },
          }}
          renderValue={(value) => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Flag countryCode={getFlagCode(value as string)} size={18} />
              <span>{currentLangData?.nativeName || value}</span>
            </Box>
          )}
          sx={{
            color: 'white',
            '& .MuiSelect-select': {
              py: 0.5,
              display: 'flex',
              alignItems: 'center',
            },
            '& .MuiSelect-icon': {
              color: 'rgba(255, 255, 255, 0.7)',
            },
            '&:before': {
              borderBottom: 'none',
            },
            '&:after': {
              borderBottom: 'none',
            },
            '&:hover:not(.Mui-disabled):before': {
              borderBottom: 'none',
            },
          }}
        >
          {supportedLanguages.map((lang) => (
            <MenuItem key={lang.code} value={lang.code}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Flag countryCode={lang.flagCode} size={18} />
                <span>{lang.nativeName}</span>
              </Box>
            </MenuItem>
          ))}
        </Select>
        {isChanging && (
          <CircularProgress size={16} sx={{ color: 'white' }} />
        )}
      </Box>
    );
  }

  // Default variant
  return (
    <Box 
      className={className}
      sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1.5,
      }}
    >
      {showLabel && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <LanguageIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
          <Typography variant="body2" color="text.secondary">
            {t('language')}:
          </Typography>
        </Box>
      )}
      <FormControl size="small">
        <Select
          value={currentLanguage}
          onChange={handleChange}
          disabled={isChanging}
          sx={{
            minWidth: 160,
            '& .MuiSelect-select': {
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            },
          }}
        >
          {supportedLanguages.map((lang) => (
            <MenuItem key={lang.code} value={lang.code}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Flag countryCode={lang.flagCode} size={18} />
                <Typography variant="body2">{lang.nativeName}</Typography>
                <Typography variant="caption" color="text.secondary">
                  ({lang.name})
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {isChanging && (
        <CircularProgress size={16} />
      )}
    </Box>
  );
}
